import {useEffect} from 'react'
import {Link as RouterLink, useNavigate} from 'react-router-dom'

import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Divider from '@mui/material/Divider'
import HoldingIcon from '@mui/icons-material/InfoOutlined'
import KeyIcon from '@mui/icons-material/VpnKeyOutlined'
import LockIcon from '@mui/icons-material/LockOutlined'
import OpenIcon from '@mui/icons-material/LockOpenOutlined'
import RecoveryIcon from '@mui/icons-material/GroupsOutlined'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'

import CopyToClipboard from '../shared/CopyToClipboard'
import InfoTile from '../shared/InfoTile'

import {connect} from '../../state/hooks/tools'
import {formatDate} from '../../lib/data'
import {isEmpty} from '../../lib/tools'
import {readEndpoint} from '../../lib/asyncMethods'
import {recoverySummary} from '../../lib/recovery'
import {PENDING_WRAPS, RECOVERY_INVITES, USER_KEY} from '../../state/actionTypes'

import './accountProtection.scss'

/**
 * The page that answers "can you read my key?", reached from Security & sign-in.
 *
 * The honest answer is not "never" — it is "only when you opened it yourself", and the page is
 * built around saying that without overclaiming. Signing in re-wraps the key to the session so
 * the account can work; signing out drops that copy; away from a session there is nothing here
 * that opens it, because the password never lands anywhere it could be kept. The last section
 * says the middle part out loud rather than leaving it to be discovered, which is the only
 * reason a user has to believe the other two.
 *
 * The fingerprint is the one claim on the page a user does not have to take on trust. It is the
 * key's own identity, so it survives a password change — the key is re-wrapped, not replaced —
 * and changes after a recovery, which generates a new pair. Someone who notes it down can check
 * both for themselves, and that is why it leads rather than closing the page.
 */
const AccountProtection = props => {
  const navigate = useNavigate()
  const {accountKey, recoveryInvites, pendingWraps} = props

  useEffect(() => {
    if (isEmpty(props.userKey))
      props.fetchUserKey('user-keys/fingerprint')
  }, [])

  useEffect(() => {
    props.fetchRecoveryInvites('recovery-invites')
    props.fetchPendingWraps('recovery-key-shares/pending')
  }, [])

  const goBack = () => {
    navigate(-1)
  }

  return (
    <Stack component='div' className='account-protection'>
      <Typography variant='h4' gutterBottom>How your account is protected</Typography>
      <Typography sx={{marginBottom: '25px'}} variant='body1' gutterBottom>
        Your account has a key of its own. Your recovery contacts hold pieces of it, and the key
        itself is locked with your password.
      </Typography>
      {
        !isEmpty(accountKey) && (
          <Box component='div' sx={{marginBottom: '15px'}}>
            <Typography variant='h6' gutterBottom>Your key</Typography>
            <Typography sx={{marginBottom: '15px'}} variant='body2' gutterBottom>
              This is the same key after a password change, and a different one after a recovery.
              Noting it down is how you check that for yourself
            </Typography>
            <InfoTile
              className='key-info'
              icon={KeyIcon}
              iconColor='primary'
              label='Fingerprint'
              details={[`Created ${formatDate(accountKey.createdAt)}`]}>
              <CopyToClipboard height='auto' text={accountKey.fingerprint} />
            </InfoTile>
          </Box>
        )
      }
      <Box component='div' sx={{marginBottom: '15px'}}>
        <Typography variant='h6' gutterBottom>When your key can be opened</Typography>
        <Typography sx={{marginBottom: '15px'}} variant='body2' gutterBottom>
          Never on its own. Something you did is what opens it, every time
        </Typography>
        <InfoTile
          icon={OpenIcon}
          iconColor='primary'
          label='While you are signed in'
          details={[
            'Your key is open, so the account can do its work — handing a contact their piece, or connecting an app you approved'
          ]}
        />
        <InfoTile
          icon={LockIcon}
          iconColor='primary'
          label='When you sign out'
          details={['It closes again. The open copy goes when your session does']}
        />
        <InfoTile
          icon={KeyIcon}
          iconColor='secondary'
          label='While you are away'
          details={['Nothing here can open it. Your password is the only thing that does, and we do not keep it']}
        />
      </Box>
      <Box component='div' sx={{marginBottom: '15px'}}>
        <Typography variant='h6' gutterBottom>Why we keep asking for your password</Typography>
        <Typography variant='body2' gutterBottom>
          You are asked for it at moments where it might seem we already know it — changing it,
          sending a contact their piece, finishing a recovery. Each of those needs your key open,
          and asking you is the only way we have to open it
        </Typography>
      </Box>
      <Box component='div' sx={{marginBottom: '15px'}}>
        <Typography variant='h6' gutterBottom>If you forget it</Typography>
        <Typography sx={{marginBottom: '15px'}} variant='body2' gutterBottom>
          We cannot unlock your key for you. There is nothing on our side that would open it, which
          is why recovery takes three of the people you chose rather than a button we can press
        </Typography>
        <InfoTile
          className='recovery-info'
          icon={RecoveryIcon}
          label='Recovery contacts'
          details={[recoverySummary({invites: recoveryInvites, pendingWraps})]}>
          <Button
            className='manage'
            component={RouterLink}
            to='/dashboard/recovery'
            size='small'>
            {isEmpty(recoveryInvites) ? 'Set up' : 'Manage'}
          </Button>
        </InfoTile>
      </Box>
      <Box component='div' sx={{marginBottom: '15px'}}>
        <Typography variant='h6' gutterBottom>What we hold</Typography>
        <InfoTile
          icon={HoldingIcon}
          iconColor='secondary'
          label='An open copy of your key, until you sign out'
          details={[
            'It is what lets the account work without asking for your password on every screen. It is tied to your session, and dropped when the session ends'
          ]}
        />
      </Box>
      <Divider />
      <Stack sx={{marginTop: '10px', flexDirection: 'row'}}>
        <Button onClick={goBack} sx={{bgcolor: 'Background'}}>
          <ArrowBackIcon />
          <Typography variant='span'>Back</Typography>
        </Button>
      </Stack>
    </Stack>
  )
}

const mapStateToProps = ({userKey, recovery, pendingWraps}) => ({
  userKey: userKey.userKey || [],
  accountKey: (userKey.userKey || [])[0] || {},
  recoveryInvites: recovery.recoveryInvites || [],
  pendingWraps: pendingWraps.pendingWraps || []
})

const actionCreators = ({userKey, recovery, pendingWraps}) => ({
  fetchUserKey: readEndpoint(userKey, USER_KEY),
  fetchRecoveryInvites: readEndpoint(recovery, RECOVERY_INVITES),
  fetchPendingWraps: readEndpoint(pendingWraps, PENDING_WRAPS)
})

export default connect(mapStateToProps, actionCreators)(AccountProtection)
